import { useMemo, useState } from "react";
import {
  WAVE_CHAT_REACTION_PALETTE,
  WaveChatAuthorKind,
  WaveChatMessageStatus,
  formatChatTime,
  resolveCommentAuthorLabel,
  type WaveChatMessage,
  type WaveDocEditor,
  type WaveParticipantRecord,
} from "@vida/wave-core";
import { WaveAuthorAvatar } from "../presence/WaveAuthorAvatar";
import { WaveIdentityHoverTooltip } from "../presence/WaveIdentityHoverTooltip";
import type { useWaveChat } from "../../hooks/useWaveChat";
import { maybeTriggerChatAgent } from "../../services/waveChatAgent";
import { renderLinkifiedText } from "../../services/waveLinkify";
import { WaveChatMentionComposer } from "./WaveChatMentionComposer";
import {
  parseChatAgentMention,
  fetchWhaleTeamAgents,
  startWhaleMissionFromChat,
} from "../../services/waveChatAgentMention";

type ChatApi = ReturnType<typeof useWaveChat>;

type MissionLink = {
  messageId: string;
  label: string;
};

type Props = {
  waveId: string;
  spaceId?: string;
  editor?: WaveDocEditor | null;
  launchedBy?: string;
  chat: ChatApi;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onShowParticipants?: () => void;
  onMissionStarted?: () => void;
  /** When true, hides the viewport-fixed FAB (e.g. tests). Default: FAB visible. */
  hideFloatingFab?: boolean;
};

function authorLabel(msg: WaveChatMessage, participants: WaveParticipantRecord[]): string {
  if (msg.authorKind === "AGENT") return msg.authorName ?? "Assistente";
  return resolveCommentAuthorLabel(
    {
      authorSub: msg.authorSub,
      authorName: msg.authorName ?? msg.authorSub,
    },
    participants,
  );
}

function ReactionBar({
  msg,
  onToggle,
}: {
  msg: WaveChatMessage;
  onToggle: (emoji: string) => void;
}) {
  return (
    <div className="wave-chat-reaction-bar">
      {WAVE_CHAT_REACTION_PALETTE.map((emoji) => {
        const count = msg.reactionCounts.find((r) => r.emoji === emoji)?.count ?? 0;
        const active = msg.viewerReactions.includes(emoji);
        if (count === 0 && !active) {
          return (
            <button
              key={emoji}
              type="button"
              className="wave-chat-reaction-btn"
              onClick={() => onToggle(emoji)}
              aria-label={`Reazione ${emoji}`}
            >
              {emoji}
            </button>
          );
        }
        return (
          <button
            key={emoji}
            type="button"
            className={`wave-chat-reaction-btn ${active ? "is-active" : ""}`}
            onClick={() => onToggle(emoji)}
          >
            {emoji} {count > 0 ? count : ""}
          </button>
        );
      })}
    </div>
  );
}

function MessageRow({
  msg,
  participants,
  missionLink,
  localOnly = false,
  onReply,
  onToggleReaction,
}: {
  msg: WaveChatMessage;
  participants: WaveParticipantRecord[];
  missionLink?: string;
  /** Risposta agente solo client-side: niente reply/reazioni (id non persistito). */
  localOnly?: boolean;
  onReply: () => void;
  onToggleReaction: (emoji: string) => void;
}) {
  const label = authorLabel(msg, participants);
  return (
    <div className="wave-chat-message-row">
      <WaveIdentityHoverTooltip dateIso={msg.createdAt} email={participants.find((p) => p.sub === msg.authorSub)?.email ?? null}>
        <WaveAuthorAvatar
          authorSub={msg.authorSub}
          authorName={label}
          participants={participants}
          size={32}
        />
      </WaveIdentityHoverTooltip>
      <div className="wave-chat-message-body">
        <div className="wave-chat-message-meta">
          <span className="wave-chat-message-author">{label}</span>
          <span className="wave-chat-message-time">{formatChatTime(msg.createdAt)}</span>
        </div>
        <p className="wave-chat-message-text">{renderLinkifiedText(msg.body)}</p>
        {missionLink && (
          <p className="wave-chat-mission-link muted">{missionLink}</p>
        )}
        {!localOnly && msg.replyCount > 0 && (
          <button type="button" className="wave-chat-replies-link" onClick={onReply}>
            {msg.replyCount} risposte
          </button>
        )}
        {!localOnly && <ReactionBar msg={msg} onToggle={onToggleReaction} />}
      </div>
      {!localOnly && (
        <button type="button" className="wave-chat-reply-btn" onClick={onReply} aria-label="Rispondi">
          ↩
        </button>
      )}
    </div>
  );
}

export function WaveChatShell({
  waveId,
  spaceId = "",
  editor = null,
  launchedBy = "",
  chat,
  open,
  onOpenChange,
  onShowParticipants,
  onMissionStarted,
  hideFloatingFab = false,
}: Props) {
  const [missionLinks, setMissionLinks] = useState<MissionLink[]>([]);
  const [agentReplies, setAgentReplies] = useState<WaveChatMessage[]>([]);
  const [agentPending, setAgentPending] = useState<{
    parentId: string | null;
    label: string;
  } | null>(null);
  const roots = useMemo(
    () =>
      [
        ...chat.messages.filter((m) => !m.parentId),
        ...agentReplies.filter((m) => !m.parentId),
      ].sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt)),
    [chat.messages, agentReplies],
  );
  const threadRoot = chat.threadParentId
    ? chat.messages.find((m) => m.messageId === chat.threadParentId) ?? roots.find((m) => m.messageId === chat.threadParentId)
    : null;
  const threadReplies = useMemo(
    () =>
      [
        ...chat.threadReplies,
        ...agentReplies.filter((m) => m.parentId === chat.threadParentId),
      ].sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt)),
    [chat.threadReplies, agentReplies, chat.threadParentId],
  );

  const isLocalAgentMessage = (messageId: string) => messageId.startsWith("local-agent-");

  const handleAgentMention = async (
    body: string,
    messageId: string,
    parentId: string | null,
  ) => {
    if (!editor || !launchedBy) return;
    const agents = await fetchWhaleTeamAgents();
    const mentioned = parseChatAgentMention(body, agents);
    if (!mentioned) return;
    setAgentPending({ parentId, label: mentioned.name });
    try {
      const result = await startWhaleMissionFromChat({
        waveId,
        spaceId,
        body,
        editor,
        launchedBy,
        agents,
      });
      if (!result) return;
      if (result.executed) {
        // Task operativo: il widget agent_task è stato creato nel documento.
        setMissionLinks((prev) => [
          ...prev,
          {
            messageId,
            label: `Missione avviata con ${result.agentName} →`,
          },
        ]);
        onMissionStarted?.();
      } else if (result.outputText.trim()) {
        // Domanda semplice: risposta inline in chat, nessun widget.
        setAgentReplies((prev) => [
          ...prev,
          {
            messageId: `local-agent-${result.jobId || crypto.randomUUID()}`,
            waveId,
            authorSub: `agent:${result.handler.agentId}`,
            authorKind: WaveChatAuthorKind.Agent,
            authorName: result.handler.agentName,
            body: result.outputText.trim(),
            parentId,
            status: WaveChatMessageStatus.Sent,
            createdAt: new Date().toISOString(),
            replyCount: 0,
            reactionCounts: [],
            viewerReactions: [],
          },
        ]);
      }
    } catch {
      /* mission errors surface in thread widget */
    } finally {
      setAgentPending(null);
    }
  };

  const sendRoot = async (body: string) => {
    const posted = await chat.postMessage(body, null);
    void maybeTriggerChatAgent({
      waveId,
      body,
      recentMessages: chat.messages,
      parentId: null,
    });
    void handleAgentMention(body, posted.messageId, null);
  };

  const sendReply = async (body: string) => {
    if (!chat.threadParentId) return;
    const posted = await chat.postMessage(body, chat.threadParentId);
    void maybeTriggerChatAgent({
      waveId,
      body,
      recentMessages: [...chat.messages, ...chat.threadReplies],
      parentId: chat.threadParentId,
    });
    void handleAgentMention(body, posted.messageId, chat.threadParentId);
  };

  return (
    <div className="wave-chat-layer" aria-live="polite">
      {chat.toast && !open && (
        <button
          type="button"
          className="wave-chat-toast"
          onClick={() => {
            chat.dismissToast();
            onOpenChange(true);
          }}
        >
          <strong>{authorLabel(chat.toast, chat.participants)}</strong>
          <span>{chat.toast.body.slice(0, 80)}</span>
        </button>
      )}

      {!open && !hideFloatingFab && (
        <button
          type="button"
          className="wave-chat-fab"
          onClick={() => onOpenChange(true)}
          aria-label="Apri chat del documento"
        >
          💬
          {chat.unread > 0 && (
            <span className="wave-chat-fab-badge">{chat.unread > 9 ? "9+" : chat.unread}</span>
          )}
        </button>
      )}

      {open && (
        <div className={`wave-chat-popover ${chat.layout === "expanded" ? "is-expanded" : ""}`}>
          <header className="wave-chat-popover-header">
            <div>
              <h3>Chat del documento</h3>
              <p>{chat.participants.length} partecipanti</p>
            </div>
            <div className="wave-chat-popover-actions">
              {onShowParticipants && (
                <button type="button" onClick={onShowParticipants} aria-label="Partecipanti">
                  👥
                </button>
              )}
              <button
                type="button"
                onClick={() =>
                  chat.setLayout(chat.layout === "compact" ? "expanded" : "compact")
                }
                aria-label="Espandi"
              >
                ⤢
              </button>
              <button type="button" onClick={() => onOpenChange(false)} aria-label="Chiudi">
                ✕
              </button>
            </div>
          </header>

          {chat.threadParentId && threadRoot ? (
            <div className="wave-chat-thread">
              <button type="button" className="wave-chat-thread-back" onClick={chat.closeThread}>
                ← Torna al canale
              </button>
              <MessageRow
                msg={threadRoot}
                participants={chat.participants}
                missionLink={missionLinks.find((m) => m.messageId === threadRoot.messageId)?.label}
                onReply={() => {}}
                onToggleReaction={(emoji) => void chat.toggleReaction(threadRoot.messageId, emoji)}
              />
              <div className="wave-chat-thread-replies">
                {threadReplies.map((reply) => (
                  <MessageRow
                    key={reply.messageId}
                    msg={reply}
                    participants={chat.participants}
                    missionLink={missionLinks.find((m) => m.messageId === reply.messageId)?.label}
                    localOnly={isLocalAgentMessage(reply.messageId)}
                    onReply={() => {}}
                    onToggleReaction={(emoji) => void chat.toggleReaction(reply.messageId, emoji)}
                  />
                ))}
                {agentPending && agentPending.parentId === chat.threadParentId && (
                  <p className="wave-chat-agent-pending muted">
                    ✨ {agentPending.label} sta rispondendo…
                  </p>
                )}
              </div>
              <WaveChatMentionComposer
                placeholder="Rispondi nel thread…"
                participants={chat.participants}
                onSend={sendReply}
              />
            </div>
          ) : (
            <>
              <div className="wave-chat-message-list">
                {chat.loading && <p className="wave-chat-loading">Caricamento…</p>}
                {roots.map((msg) => (
                  <MessageRow
                    key={msg.messageId}
                    msg={msg}
                    participants={chat.participants}
                    missionLink={missionLinks.find((m) => m.messageId === msg.messageId)?.label}
                    localOnly={isLocalAgentMessage(msg.messageId)}
                    onReply={() => void chat.openThread(msg.messageId)}
                    onToggleReaction={(emoji) => void chat.toggleReaction(msg.messageId, emoji)}
                  />
                ))}
                {agentPending && agentPending.parentId === null && (
                  <p className="wave-chat-agent-pending muted">
                    ✨ {agentPending.label} sta rispondendo…
                  </p>
                )}
              </div>
              <WaveChatMentionComposer
                placeholder="Scrivi un messaggio… (@ per menzionare)"
                participants={chat.participants}
                onSend={sendRoot}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}
